import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import classNames from "classnames";
import _ from "lodash";
import LoadingSpinner from "../../elements/loading-spinner";
import Car360FrameLoader from "../../utils/car-360-frame-loader";
import "./style.scss";
import { mobileIs } from "../../utils/siteUtils";
const isMobile = mobileIs()
let _currentFrame = 0;
let _loading = false;
let _loaded = false;
const CarExteriorViewer = ({ fields, modelIndex, onModelChange = () => false, viewerName = '' }) => {
  const { models, bg, leftArrow, rightArrow, icon } = fields;
  const [percent, setPercent] = useState("0%");
  const [loading, setLoading] = useState(false);
  const [currentFrame, setCurrentFrame] = useState(_currentFrame);
  const viewing = useRef(false);
  const touchPosition = useRef({ x: 0, y: 0 });
  const mouseX = useRef(0);
  const stageNode = useRef(null);
  const slidesNode = useRef(null);
  const frameLoader = useRef(null);
  const [currentBg, setCurrentBg] = useState('');
  const isViewerV2 = viewerName === 'Car360ViewV2';
  const [iconPosition, setIconPosition] = useState({
    top: '0',
    left: '0'
  });
  const rootEl = useRef();
  const [showIcon, setShowIcon] = useState(false)
  const frames = useMemo(() => getFrameImages(models, modelIndex, isViewerV2), [modelIndex]);
  const toPrevFrame = useCallback(() => {
    if (_currentFrame >= frames.length - 1) {
      _currentFrame = 0;
    } else {
      _currentFrame += 1;
    }
    setCurrentFrame(_currentFrame);
  }, [currentFrame]);
  const toNextFrame = useCallback(() => {
    if (_currentFrame <= 0) {
      _currentFrame = frames.length - 1;
    } else {
      _currentFrame -= 1;
    }
    setCurrentFrame(_currentFrame);
  }, [currentFrame]);
  const checkLoading = () => {
    if (stageNode.current) {
      const rect = stageNode.current.getBoundingClientRect();
      if (rect.top <= window.innerHeight && !_loading) {
        loadModel();
      }
    }
  };
  const loadModel = () => {
    setLoading(true);
    _loading = true;
    _currentFrame = 0;
    setCurrentFrame(0);
    setPercent("0%");
    frameLoader.current = new Car360FrameLoader(frames, {
      onProgress(percent) {
        setPercent(`${percent}%`);
      },
      onComplete() {
        setLoading(false);
        _loading = false;
        _loaded = true;
      }
    });
  };
  const stageProps = {
    ref: stageNode,
    onMouseDown(e) {
      viewing.current = true;
      mouseX.current = e.pageX;
    },
    onMouseLeave() {
      viewing.current = false;
    },
    onMouseUp() {
      viewing.current = false;
    },
    onTouchStart(e) {
      viewing.current = true;
      touchPosition.current = {
        x: e.touches[0].pageX,
        y: e.touches[0].pageY
      };
    },
    onTouchEnd() {
      viewing.current = false;
    }
  };
  // 鼠标移动
  const mousemove = (e) => {
    const eventX = e.clientX || (e.touches && e.touches[0]?.clientX) || 0
    const eventY = e.clientY || (e.touches && e.touches[0]?.clientY) || 0
    const container = rootEl.current;
    if (!container) return;

    const containerRect = container.getBoundingClientRect();
    const containerTop = containerRect.top;
    const containerHeight = containerRect.height;
    const containerLeft = containerRect.left;
    const containerWidth = containerRect.width;
    let relativeY = eventY - containerTop;
    const minY = 0;
    const maxY = containerHeight;
    const constrainedY = Math.max(minY, Math.min(relativeY, maxY));

    let relativeX = eventX - containerLeft;
    const minX = 0;
    const maxX = containerWidth - 72;
    const constrainedX = Math.max(minX, Math.min(relativeX, maxX));
    setIconPosition({
      top: `${constrainedY - 36}px`,
      left: `${constrainedX - 36}px`
    });
  }
  // 鼠标移出
  const mouseLeave = (e) => {
    setShowIcon(false)
  }

  // 鼠标移入
  const mouseOver = (e) => {
    setShowIcon(true)
  }
  useEffect(() => {
    const onScroll = _.debounce(e => {
      if (!_loaded && !isViewerV2) {
        checkLoading();
      }
    }, 50);
    const onStageMousemove = _.throttle(e => {
      if (viewing.current) {
        e.pageX > mouseX.current ? toNextFrame() : toPrevFrame();
        mouseX.current = e.pageX;
      }
    }, 50);
    const onStageTouchmove = _.throttle(e => {
      if (viewing.current) {
        const x = e.touches[0].pageX,
          y = e.touches[0].pageY;
        const angle = (180 * Math.atan((y - touchPosition.current.y) / (x - touchPosition.current.x))) / Math.PI;
        if (Math.abs(angle) < 30) {
          if (x > touchPosition.current.x) {
            console.log("to next");
            toNextFrame();
          } else {
            console.log("to prev");
            toPrevFrame();
          }
        }
        touchPosition.current = {
          x: e.touches[0].pageX,
          y: e.touches[0].pageY
        };
      }
    }, 50);
    const onKeydown = _.throttle(e => {
      if (_loaded) {
        if (e.keyCode == 37) {
          console.log("to prev");
          toPrevFrame();
        }
        if (e.keyCode == 39) {
          console.log("to next");
          toNextFrame();
        }
      }
    }, 1000 / 24);
    if (isViewerV2 && isMobile) {
      rootEl.current.addEventListener("touchmove", onStageTouchmove);
    }
    slidesNode.current.addEventListener("mousemove", onStageMousemove);
    slidesNode.current.addEventListener("touchmove", onStageTouchmove);
    document.addEventListener("keydown", onKeydown);
    window.addEventListener("scroll", onScroll);
    return () => {
      if (isViewerV2 && isMobile) {
        rootEl.current.removeEventListener("touchmove", onStageTouchmove);
      }
      slidesNode.current.removeEventListener("mousemove", onStageMousemove);
      slidesNode.current.removeEventListener("touchmove", onStageTouchmove);
      document.removeEventListener("keydown", onKeydown);
      window.removeEventListener("scroll", onScroll);
    };
  }, []);
  useEffect(() => {
    if (frameLoader.current) {
      frameLoader.current.destroy();
    }
    _loaded = false;
    checkLoading();
  }, [modelIndex]);

  // preload images
  useEffect(() => {
    if (isViewerV2) {
      models.forEach(item => {
        const img = new Image();
        img.src = item.fields.bgImage.value.src;
        window[img.src] = img;
      });

      return () => {
        models.forEach(item => {
          const src = item.fields.bgImage.value.src;
          if (window[src]) delete window[src];
        });
      };
    }
  }, [models]);
  useEffect(() => {
    if (isViewerV2) {

      const currentSrc = models[modelIndex]?.fields?.bgImage?.value?.src;
      if (currentSrc) {
        setCurrentBg(currentSrc);
      }
    }
  }, [modelIndex, models]);
  if (isViewerV2) {
    return (
      <div className="s-car-exterior-viewer" style={{ backgroundImage: `url(${currentBg})` }}>
        <div className="s-car-exterior-viewer">
          {loading && (
            <div className="s-car-exterior-viewer__loading">
              <div className="s-car-exterior-viewer__loading-content">
                <LoadingSpinner />
                <div className="s-car-exterior-viewer__percent">{percent}</div>
              </div>
            </div>
          )}
          <div className="s-car-exterior-viewer__stage" {...stageProps}>
            <div className="s-car-exterior-viewer__slides" ref={rootEl} onMouseMove={mousemove} onMouseLeave={mouseLeave} onMouseOver={mouseOver}>
              <div className={["rotate-icon", showIcon ? 'show' : 'hidden'].join(' ')} ref={slidesNode} style={{
                top: iconPosition.top,
                left: iconPosition.left,
              }}>
                <svg width="45" height="34" viewBox="0 0 45 34" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M35.814 17.4346L34.7761 19.8019C37.0248 20.8165 38.4086 22.0002 38.4086 23.0148C38.4086 25.213 32.7004 27.9186 24.0517 28.4259L24.2247 30.9624C32.5275 30.4551 41.0032 27.7495 41.0032 23.0148C41.0032 21.4929 39.9654 19.2946 35.814 17.4346ZM17.1327 28.2568C15.23 28.0877 13.1543 27.7495 11.5975 27.5804C10.2137 27.2422 8.8299 26.904 7.61907 26.5658C4.6785 25.5512 2.77578 24.1985 2.77578 23.0148C2.77578 22.0002 3.9866 20.8165 6.23527 19.971L5.19742 17.6037C1.04603 19.4637 0.181152 21.4929 0.181152 23.0148C0.181152 24.7058 1.39198 27.0731 6.7542 28.9332C8.138 29.4405 9.5218 29.7787 11.0786 30.1169C12.8083 30.4551 15.057 30.7933 17.1327 30.9624V33.1606L22.3219 29.7787L17.1327 26.3967V28.2568Z" fill="white" />
                  <path d="M9.42286 7.34888C10.0764 7.47702 10.6658 7.7397 11.1912 8.13693C11.7165 8.52134 12.1266 9.01468 12.4213 9.61692C12.7288 10.2064 12.8826 10.8535 12.8826 11.5582V11.712C12.8826 12.5193 12.6712 13.2496 12.2483 13.9031C11.8383 14.5438 11.2809 15.05 10.5761 15.4216C9.87134 15.7932 9.10892 15.979 8.28884 15.979H8.19273C7.39828 15.979 6.65508 15.8188 5.96313 15.4985C5.27118 15.1653 4.70097 14.704 4.25249 14.1146C3.81682 13.5123 3.57335 12.8332 3.5221 12.0772H5.84781C5.91188 12.6538 6.16174 13.1343 6.59741 13.5187C7.03308 13.9031 7.56486 14.0954 8.19273 14.0954H8.28884C8.86546 14.0954 9.34598 13.9416 9.73039 13.6341C10.1276 13.3265 10.4095 12.9549 10.5761 12.5193C10.6145 12.3014 10.6338 12.0067 10.6338 11.6351C10.6338 11.2122 10.6145 10.9175 10.5761 10.7509C10.4095 10.3025 10.134 9.93086 9.74961 9.63615C9.37801 9.32861 8.89109 9.17485 8.28884 9.17485H5.86703L8.92312 4.6772H4.19482V2.62058H12.6135V2.65902L9.42286 7.34888ZM20.0413 7.13745C20.8101 7.13745 21.5213 7.32325 22.1748 7.69485C22.8411 8.06645 23.3664 8.57901 23.7509 9.23251C24.1481 9.88601 24.3467 10.6228 24.3467 11.4429V11.5967C24.3467 12.4296 24.1417 13.1792 23.7316 13.8455C23.3216 14.5118 22.7642 15.0372 22.0594 15.4216C21.3675 15.7932 20.6115 15.979 19.7914 15.979H19.6761C18.8688 15.979 18.1128 15.7932 17.408 15.4216C16.7161 15.0372 16.1587 14.5118 15.7358 13.8455C15.3258 13.1792 15.1208 12.4296 15.1208 11.5967V11.4429C15.1208 11.0072 15.1912 10.5587 15.3322 10.0974C15.4731 9.63615 15.7038 9.12359 16.0241 8.55978L19.3685 2.62058H21.7903L19.1379 7.23355C19.4326 7.16949 19.7017 7.13745 19.9452 7.13745H20.0413ZM22.0594 12.4616C22.0851 12.2566 22.0979 11.9426 22.0979 11.5198C22.0979 11.0713 22.0851 10.7574 22.0594 10.578C21.9441 10.1551 21.675 9.7771 21.2522 9.44394C20.8421 9.11078 20.3552 8.9442 19.7914 8.9442H19.6761C19.1123 8.9442 18.6189 9.11078 18.1961 9.44394C17.786 9.7771 17.5298 10.1551 17.4272 10.578C17.4016 10.7574 17.3888 11.0713 17.3888 11.5198C17.3888 11.9426 17.4016 12.2566 17.4272 12.4616C17.5298 12.8844 17.786 13.2625 18.1961 13.5956C18.6189 13.9288 19.1123 14.0954 19.6761 14.0954H19.7914C20.3552 14.0954 20.8421 13.9288 21.2522 13.5956C21.675 13.2625 21.9441 12.8844 22.0594 12.4616ZM31.3208 15.979C30.4367 15.979 29.623 15.7547 28.8798 15.3063C28.1366 14.8578 27.5472 14.2107 27.1115 13.365C26.6758 12.5064 26.458 11.5006 26.458 10.3473V7.96394C26.458 6.8107 26.6758 5.81122 27.1115 4.96551C27.5472 4.10698 28.1366 3.45348 28.8798 3.005C29.623 2.55651 30.4367 2.33227 31.3208 2.33227H31.4554C32.3395 2.33227 33.1532 2.55651 33.8964 3.005C34.6396 3.45348 35.229 4.10698 35.6647 4.96551C36.1004 5.81122 36.3182 6.8107 36.3182 7.96394V10.3473C36.3182 11.5006 36.1004 12.5064 35.6647 13.365C35.229 14.2107 34.6396 14.8578 33.8964 15.3063C33.1532 15.7547 32.3395 15.979 31.4554 15.979H31.3208ZM31.4554 14.0377C32.0832 14.0377 32.6086 13.8391 33.0315 13.4418C33.4671 13.0318 33.749 12.5833 33.8772 12.0964C33.9284 11.6735 33.9541 10.6933 33.9541 9.15563C33.9541 7.61797 33.9284 6.63771 33.8772 6.21486C33.749 5.72793 33.4671 5.28585 33.0315 4.88863C32.6086 4.47858 32.0832 4.27356 31.4554 4.27356H31.3208C30.6801 4.27356 30.142 4.47858 29.7063 4.88863C29.2834 5.28585 29.0079 5.72793 28.8798 6.21486C28.8413 6.68897 28.8221 7.66922 28.8221 9.15563C28.8221 10.6292 28.8413 11.6095 28.8798 12.0964C29.0079 12.5833 29.2834 13.0318 29.7063 13.4418C30.142 13.8391 30.6801 14.0377 31.3208 14.0377H31.4554ZM41.7943 7.52187C41.2561 7.52187 40.7564 7.39373 40.2951 7.13745C39.8466 6.88117 39.4878 6.5352 39.2187 6.09953C38.9625 5.65105 38.8343 5.16412 38.8343 4.63876C38.8343 4.11339 38.9625 3.63287 39.2187 3.1972C39.4878 2.74872 39.8466 2.39634 40.2951 2.14006C40.7564 1.88379 41.2561 1.75565 41.7943 1.75565C42.3325 1.75565 42.8258 1.88379 43.2743 2.14006C43.7228 2.39634 44.0752 2.74872 44.3314 3.1972C44.6005 3.63287 44.7351 4.11339 44.7351 4.63876C44.7351 5.16412 44.6005 5.65105 44.3314 6.09953C44.0752 6.5352 43.7228 6.88117 43.2743 7.13745C42.8258 7.39373 42.3325 7.52187 41.7943 7.52187ZM41.7943 6.11875C42.1915 6.11875 42.5311 5.9778 42.813 5.6959C43.0949 5.40118 43.2359 5.0488 43.2359 4.63876C43.2359 4.22871 43.0949 3.87633 42.813 3.58162C42.5311 3.2869 42.1915 3.13954 41.7943 3.13954C41.3843 3.13954 41.0383 3.2869 40.7564 3.58162C40.4745 3.87633 40.3335 4.22871 40.3335 4.63876C40.3335 5.0488 40.4745 5.40118 40.7564 5.6959C41.0383 5.9778 41.3843 6.11875 41.7943 6.11875Z" fill="white" />
                </svg>
              </div>
              {frames.map((frame, index) => (
                <img src={frame} key={index} className={classNames("s-car-exterior-viewer__slide", { active: currentFrame === index })} onClick={toNextFrame} />
              ))}
            </div>
          </div>
          <div className="c-car360-view-v2__color">
            {
              models && models.map((model, index) => {
                const { switcherColorLeft, switcherColorRight, switcherColorText } = model.fields;
                const isActive = index === modelIndex;
                return (
                  <div key={model.id} className="color-container">

                    {isActive && <div className="color-line" ></div>}
                    <div className={`btn ${isActive ? 'active' : ''}`} onClick={(e) => onModelChange(e, index)} >
                      <div className="outer-circle" style={{ border: isActive ? `1px solid ${switcherColorLeft.value.slice(0, 7)}` : 'none' }}></div>
                      <div className="inner-circle" style={{ background: `linear-gradient(144.29deg, ${switcherColorLeft.value}, ${switcherColorRight.value})` }}></div>
                    </div>
                    <div className={`color-name ${isActive ? 'fade' : ''} ${index === 0 ? 'active' : ''}`} >{switcherColorText.value}</div>
                  </div>
                )
              })
            }
          </div>
        </div>
      </div>
    )
  }
  return (
    <div className="s-car-exterior-viewer" style={{ backgroundImage: `url(${bg.value.src})` }}>
      {loading && (
        <div className="s-car-exterior-viewer__loading">
          <div className="s-car-exterior-viewer__loading-content">
            <LoadingSpinner />
            <div className="s-car-exterior-viewer__percent">{percent}</div>
          </div>
        </div>
      )}
      <div className="s-car-exterior-viewer__stage" {...stageProps}>
        <img src={leftArrow.value.src} className="s-car-exterior-viewer__left-arrow" onClick={toNextFrame} />
        <img src={rightArrow.value.src} className="s-car-exterior-viewer__right-arrow" onClick={toPrevFrame} />
        <div className="s-car-exterior-viewer__slides" ref={slidesNode}>
          {frames.map((frame, index) => (
            <img src={frame} key={index} className={classNames("s-car-exterior-viewer__slide", { active: currentFrame === index })} onClick={toNextFrame} />
          ))}
        </div>
      </div>
      <div className="s-car-exterior-viewer__models">
        {models.map((model, i) => (
          <div className={classNames("s-car-exterior-viewer__model", { active: i === modelIndex })} key={i} onClick={e => onModelChange(e, i)}>
            <img src={model.fields.exteriorThumb.value.src} />
            <div className="s-car-exterior-viewer__model-name">
              {model.fields.modelType.fields.text.value} {model.fields.color.fields.text.value}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
const getFrameImages = (models, modelIndex, isViewerV2) => {
  const frames = [];
  if (isViewerV2) {
    const exteriorFrames = models[modelIndex].fields.exteriorFrames;
    for (let i = 1; i <= exteriorFrames.length; i++) {
      let img = exteriorFrames[i - 1].fields.frameImage.value.src;
      img && frames.push(img);
    }
  } else {
    for (let i = 1; i <= 24; i++) {
      let img = models[modelIndex].fields[`exteriorFrame${i.toString().padStart(2, "0")}`].value.src;
      img && frames.push(img);
    }
  }

  return frames;
};
export default CarExteriorViewer;
